import { useEffect, useRef } from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { fetchTicket } from 'flow/actions/tickets-action';
import { addTicketLogEntry } from 'flow/utils/browser-utils';
import { TicketKey } from 'flow/types/EntityKeys';

const MAX_RETRIES = 3;

interface UseTicketFetcherOptions {
    ticketKey: TicketKey;
    ticketIsFetched: boolean;
}

const DELAY_INITIAL_FETCH_MS = 500;

/**
 * Custom hook to handle ticket fetching with exponential backoff retry logic.
 * Automatically fetches a ticket if it hasn't been fetched yet, with retry
 * mechanism for 404 errors (common when ticket creation and SIP invite race).
 */
export const useTicketFetcher = ({
    ticketKey,
    ticketIsFetched,
}: UseTicketFetcherOptions) => {
    const dispatch = useAppDispatch();
    const fetchTicketTimeout = useRef<NodeJS.Timeout>();
    const retryCount = useRef(0);
    const ticketIsFetchedRef = useRef(ticketIsFetched);

    useEffect(() => {
        // Update ref whenever the prop changes
        ticketIsFetchedRef.current = ticketIsFetched;
    }, [ticketIsFetched]);

    useEffect(() => {
        const fetchWithRetry = (attempt: number) => {
            dispatch(fetchTicket(ticketKey)).catch((error) => {
                if (error.status === 404 && attempt < MAX_RETRIES) {
                    const newAttempt = attempt + 1;
                    const delay = Math.pow(2, attempt) * 1000;
                    retryCount.current = newAttempt;

                    // Clear any existing timeout before scheduling a new one
                    if (fetchTicketTimeout.current) {
                        clearTimeout(fetchTicketTimeout.current);
                    }

                    fetchTicketTimeout.current = setTimeout(
                        () => fetchWithRetry(newAttempt),
                        delay,
                    );
                } else if (error.status) {
                    addTicketLogEntry(
                        `Ticket list: failed fetching ticket ${ticketKey}. Error ${error.status}`,
                    );
                }
            });
        };

        if (!ticketIsFetched) {
            retryCount.current = 0;
            // When we end up here, i.e. having a ticketKey but ticket is not fetched yet,
            // it's because we've received a ticketKey in a SIP invite, and sometimes fetching the ticket right away fails.
            // Therefore, we have the initial delay (...and then, should the fetch still fail due to 404 not found, the retry mechanism).

            // Clear any existing timeout before scheduling a new one
            if (fetchTicketTimeout.current) {
                clearTimeout(fetchTicketTimeout.current);
            }

            fetchTicketTimeout.current = setTimeout(() => {
                if (!ticketIsFetchedRef.current) {
                    // This extra check in case the ticket got fetched by some other action in the meantime (see onTopEventPushOfTypeTicketCreatedReceived)
                    // Note that we're using a ref so that we don't check the old value captured in the setTimeout callback
                    fetchWithRetry(retryCount.current);
                }
            }, DELAY_INITIAL_FETCH_MS);
        }

        return () => {
            if (fetchTicketTimeout.current) {
                clearTimeout(fetchTicketTimeout.current);
            }
            retryCount.current = 0;
        };
    }, [dispatch, ticketKey, ticketIsFetched]);
};
